import {
  type EnhancedDimension,
  type MediumIcon,
  dimensions as gammaDimensions,
} from '@factorialco/deprecated-design-system'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { dateToString, formatBackendDateToMonthYear } from 'modules/core/lib/dates'
import type { FormatTypes } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

export declare type ViewDimension = EnhancedDimension | 's170'
export declare type ViewType = 'daily' | 'weekly' | 'four-weeks' | 'monthly'
export declare type GroupType = 'employees' | 'areas'
export declare type RouteOption =
  | 'daily/employees'
  | 'weekly/employees'
  | 'four-weeks/employees'
  | 'monthly/employees'
  | 'weekly/areas'

export declare interface ViewByGroup {
  view: ViewType
  enabled: () => boolean
}

export declare interface GroupOption {
  group: GroupType
  enabled: () => boolean
  icon: MediumIcon
  children: ViewByGroup[]
}

export const VIEW_TYPES = Object.freeze({
  daily: 'daily',
  weekly: 'weekly',
  fourWeeks: 'four-weeks',
  monthly: 'monthly',
})

export const isValidViewType = (value: string | undefined): value is ViewType => {
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  return Object.values(VIEW_TYPES).includes(value as ViewType)
}

export const GROUP_TYPES = Object.freeze({
  employees: 'employees',
  areas: 'areas',
})

export const isValidGroupType = (value: string | undefined): value is GroupType => {
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  return Object.values(GROUP_TYPES).includes(value as GroupType)
}

export const DEFAULT_VIEW_TYPE = VIEW_TYPES.weekly
export const DEFAULT_GROUP_TYPE = GROUP_TYPES.employees

// WARNING! This is the source of truth in order to enable a view or a group
// updating this constant, we are updating the following:
// - Allowed views by flags
// - Visibility and Order of options in the view selector
export const ALLOWED_VIEWS_BY_GROUP: GroupOption[] = [
  {
    group: GROUP_TYPES.employees,
    enabled: () => true,
    icon: 'person',
    children: [
      {
        view: VIEW_TYPES.daily,
        enabled: () => hasFeature('shift_management_daily_view'),
      },
      {
        view: VIEW_TYPES.weekly,
        enabled: () => true,
      },
      {
        view: VIEW_TYPES.fourWeeks,
        enabled: () => hasFeature('shift_management_monthly_view'),
      },
      {
        view: VIEW_TYPES.monthly,
        enabled: () => hasFeature('shift_management_monthly_view'),
      },
    ],
  },
  {
    group: GROUP_TYPES.areas,
    enabled: () => hasFeature('shift_management_area_view'),
    icon: 'space-control',
    children: [
      {
        view: VIEW_TYPES.weekly,
        enabled: () => true,
      },
    ],
  },
]

// Override Gamma's dimensions because gamma does not have s170 as a dimension size
// which is necessary to accommodate the new cell design
export const overrideGammaDimensions = () => ({
  ...gammaDimensions,
  s170: '170px',
})

interface Props {
  date: moment.Moment
  viewType?: ViewType | undefined
  groupType?: GroupType | undefined
  showCoverageSection?: boolean
}

class View {
  date: moment.Moment
  viewType: ViewType
  groupType: GroupType
  showCoverageSection: boolean

  constructor({ date, viewType, groupType }: Props) {
    this.date = date

    const isValidGroupType = groupType && this.allowedGroups.has(groupType)
    this.groupType = isValidGroupType ? groupType : DEFAULT_GROUP_TYPE

    const isViewAllowed = viewType && this.allowedViews[this.groupType]?.has(viewType)
    this.viewType = isViewAllowed ? viewType : DEFAULT_VIEW_TYPE

    this.showCoverageSection = false

    makeObservable(this, {
      allowedGroups: computed,
      allowedViews: computed,
      isDaily: computed,
      isWeekly: computed,
      isMonthly: computed,
      isFourWeeks: computed,
      isByEmployees: computed,
      isByAreas: computed,
      isFullVersion: computed,
      columnUnit: computed,
      period: computed,
      dates: computed,
      firstWeekNumber: computed,
      lastWeekNumber: computed,
      startAt: computed,
      endAt: computed,
      firstDay: computed,
      lastDay: computed,
      previousPeriod: computed,
      nextPeriod: computed,
      formattedStartRange: computed,
      formattedEndRange: computed,
      formattedMonthYear: computed,
      formattedDay: computed,
      startRangeYear: computed,
      endRangeYear: computed,
      formattedStartMonthOnly: computed,
      displayRange: computed,
      headerTitle: computed,
      columnMinWidth: computed,
      cellMaxWidth: computed,
      cellMinHeight: computed,
      dateFormat: computed,
      currentDate: computed,
      currentDateString: computed,
      weeksInPeriod: computed,
    })
  }

  // Returns Set('employees', 'areas')
  get allowedGroups(): Set<string> {
    return new Set(
      ALLOWED_VIEWS_BY_GROUP.reduce<string[]>((result, { group, enabled }) => {
        if (enabled()) result.push(group)
        return result
      }, [])
    )
  }

  // Returns {'employees': Set('weekly', 'monthly'), 'areas': Set(...)
  get allowedViews(): Record<string, Set<string>> {
    return ALLOWED_VIEWS_BY_GROUP.reduce<Record<string, Set<string>>>(
      (result, { group, enabled, children }) => {
        if (enabled()) {
          const views = children.reduce<string[]>((acc, { view, enabled }) => {
            if (enabled()) acc.push(view)
            return acc
          }, [])

          result[group] = new Set(views)
        }
        return result
      },
      {}
    )
  }

  get isDaily(): boolean {
    return this.viewType === VIEW_TYPES.daily
  }

  get isWeekly(): boolean {
    return this.viewType === VIEW_TYPES.weekly
  }

  get isMonthly(): boolean {
    return this.viewType === VIEW_TYPES.monthly
  }

  get isFourWeeks(): boolean {
    return this.viewType === VIEW_TYPES.fourWeeks
  }

  get isByEmployees(): boolean {
    return this.groupType === GROUP_TYPES.employees
  }

  get isByAreas(): boolean {
    return this.groupType === GROUP_TYPES.areas
  }

  get isFullVersion(): boolean {
    return this.isDaily || this.isWeekly
  }

  get columnUnit(): moment.unitOfTime.DurationConstructor {
    return this.isDaily ? 'hour' : 'day'
  }

  get period(): moment.unitOfTime.DurationConstructor {
    if (this.isDaily) return 'day'
    if (this.isMonthly) return 'month'

    return 'week'
  }

  get dates(): moment.Moment[] {
    const days: moment.Moment[] = []
    let day = this.firstDay

    while (day <= this.lastDay) {
      days.push(day)
      day = day.clone().add(1, this.columnUnit)
    }

    return days
  }

  get firstWeekNumber(): number {
    return this.firstDay.week()
  }

  get lastWeekNumber(): number {
    return this.lastDay.week()
  }

  get firstDay(): moment.Moment {
    if (this.isDaily) return this.date.clone().startOf('day')
    if (this.isMonthly) return this.date.clone().startOf('month')

    return this.date.clone().startOf('week')
  }

  get lastDay(): moment.Moment {
    if (this.isDaily) return this.date.clone().endOf('day')
    if (this.isMonthly) return this.date.clone().endOf('month')
    if (this.isFourWeeks) return this.date.clone().endOf('week').add(3, 'weeks')

    return this.date.clone().endOf('week')
  }

  get startAt(): string {
    return dateToString(this.firstDay, 'backend')
  }

  get endAt(): string {
    return dateToString(this.lastDay, 'backend')
  }

  get weeksInPeriod(): { startAt: string; endAt: string }[] {
    if (this.isWeekly) return [{ startAt: this.startAt, endAt: this.endAt }]

    if (this.isFourWeeks) {
      const weeks = Array.from({ length: 4 }, (_, i) => {
        const start = this.firstDay.clone().add(i, 'weeks')
        return {
          startAt: start.format('YYYY-MM-DD'),
          endAt: start.clone().endOf('week').format('YYYY-MM-DD'),
        }
      })

      return weeks
    }

    // Monthly view
    const start = moment(this.firstDay)
    const end = moment(this.lastDay)
    const weeks: { startAt: string; endAt: string }[] = []

    const currentWeekStart = start.clone().startOf('week')

    while (currentWeekStart.isSameOrBefore(end)) {
      const currentWeekEnd = currentWeekStart.clone().endOf('week')

      if (currentWeekEnd.isSameOrAfter(start)) {
        weeks.push({
          startAt: currentWeekStart.format('YYYY-MM-DD'),
          endAt: currentWeekEnd.format('YYYY-MM-DD'),
        })
      }
      currentWeekStart.add(1, 'week')
    }

    return weeks
  }

  get previousPeriod(): View {
    if (this.isDaily) return new View({ ...this, date: this.date.clone().subtract(1, 'day') })
    if (this.isMonthly) return new View({ ...this, date: this.date.clone().subtract(1, 'month') })
    if (this.isFourWeeks) return new View({ ...this, date: this.date.clone().subtract(28, 'days') })

    return new View({ ...this, date: this.date.clone().subtract(7, 'days') })
  }

  get nextPeriod(): View {
    if (this.isDaily) return new View({ ...this, date: this.date.clone().add(1, 'day') })
    if (this.isMonthly) return new View({ ...this, date: this.date.clone().add(1, 'month') })
    if (this.isFourWeeks) return new View({ ...this, date: this.date.clone().add(28, 'days') })

    return new View({ ...this, date: this.date.clone().add(7, 'days') })
  }

  specificPeriod(start: moment.Moment): View {
    if (this.isDaily) return new View({ ...this, date: start })
    if (this.isMonthly) return new View({ ...this, date: start.startOf('month') })
    if (this.isFourWeeks) return new View({ ...this, date: start.startOf('week') })

    return new View({ ...this, date: start.startOf('week') })
  }

  get formattedStartRange(): string {
    return formatBackendDateToMonthYear(this.firstDay)
  }

  get formattedEndRange(): string {
    return formatBackendDateToMonthYear(this.lastDay)
  }

  get formattedMonthYear(): string {
    return this.firstDay.format('MMMM YYYY')
  }

  get formattedDay(): string {
    return this.firstDay.format('dddd, LL')
  }

  get startRangeYear(): number {
    return this.firstDay.year()
  }

  get startRangeMonth(): number {
    return this.firstDay.month() + 1
  }

  get startRangeDay(): number {
    return this.firstDay.date()
  }

  get endRangeYear(): number {
    return this.lastDay.year()
  }

  get formattedStartMonthOnly(): string {
    return this.firstDay.format('MMM')
  }

  get displayRange(): string {
    if (this.formattedStartRange === this.formattedEndRange) {
      return this.formattedMonthYear
    }

    if (this.startRangeYear === this.endRangeYear) {
      return this.formattedStartMonthOnly?.concat(' - ', this.formattedEndRange)
    }

    return this.formattedStartRange.concat(' - ', this.formattedEndRange)
  }

  get headerTitle(): string {
    if (this.isDaily) return this.formattedDay
    if (this.isMonthly) return this.displayRange

    if (this.isFourWeeks) {
      const weeks = `${this.firstWeekNumber}-${this.lastWeekNumber}`
      return `${i18n.t('formatter.week')} ${weeks}: ${this.displayRange}`
    }

    return `${i18n.t('formatter.week')} ${this.firstWeekNumber}: ${this.displayRange}`
  }

  get columnMinWidth(): ViewDimension {
    if (!this.isWeekly) return users.current.isTimeFormat24H ? 's64' : 's80'

    return users.current.isTimeFormat24H ? 's140' : 's170'
  }

  get cellMaxWidth(): ViewDimension {
    if (this.isDaily) return 'none'
    if (!this.isWeekly) return users.current.isTimeFormat24H ? 's64' : 's80'

    return users.current.isTimeFormat24H ? 's140' : 's170'
  }

  get cellMinHeight(): EnhancedDimension {
    return 's32'
  }

  get cellMaxHeight(): EnhancedDimension {
    return 's48'
  }

  // Returns a different format to consider a datetime or only the date depending on the view
  get dateFormat(): FormatTypes {
    return this.isDaily ? 'ui_short_with_time' : 'backend'
  }

  get currentDate(): moment.Moment {
    return this.date.isSame(moment(), this.period) ? moment() : this.firstDay
  }

  get currentDateString(): string {
    return dateToString(this.currentDate, 'backend')
  }

  isDateFirstDayOfPeriod(date: moment.Moment): boolean {
    const formattedDate = dateToString(date, this.dateFormat)
    return formattedDate === dateToString(this.firstDay, this.dateFormat)
  }
}

export default View
